import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';
import { trackPurchase } from '@/utils/metaPixel';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;

export default function PayPalSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);
  const [invitationCode, setInvitationCode] = useState(null);
  const [isUpgrade, setIsUpgrade] = useState(false);
  
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  
  useEffect(() => {
    if (paymentId && payerId) {
      capturePayment();
    } else {
      setStatus('error');
      setError('Missing payment information. Please try again.');
    }
  }, [paymentId, payerId]);
  
  const capturePayment = async () => {
    try {
      const response = await axios.get(`${API}/paypal/capture?paymentId=${paymentId}&PayerID=${payerId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        const isUpgrade = data.access_type === 'upgrade' || (data.redirect_url && data.redirect_url.startsWith('/chat'));

        // Track purchase event with dynamic amount
        const purchaseAmount = data.amount ? parseFloat(data.amount) :
                               isUpgrade ? 88.00 :
                               data.access_type === 'trial' ? 9.00 : 97.00;
        const productName = isUpgrade ? 'Dea Lifetime Upgrade' :
                            data.access_type === 'trial' ? 'Dea 7-Day Trial' :
                            'Dea Lifetime Access';

        trackPurchase({
          value: purchaseAmount,
          currency: 'USD',
          content_name: productName,
          content_type: 'product',
          transaction_id: paymentId
        });

        toast.success('Payment successful!');
        setStatus('success');

        if (isUpgrade) {
          // Existing user upgraded — go straight to chat
          setIsUpgrade(true);
          setTimeout(() => {
            navigate(data.redirect_url || '/chat');
          }, 3000);
        } else if (data.invitation_code) {
          // New customer — pass invitation code to signup
          setInvitationCode(data.invitation_code);
          setTimeout(() => {
            navigate(`/signup?code=${data.invitation_code}`);
          }, 3000);
        } else {
          setStatus('error');
          setError('Payment confirmed but no follow-up action received. Please contact support.');
        }
      } else {
        setStatus('error');
        setError('Payment could not be verified. Please contact support.');
      }
    } catch (err) {
      console.error('PayPal capture error:', err);
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to complete payment. Please contact support.');
    }
  };
  
  return (
    <>
      <SEO 
        title="Processing Payment"
        description="Processing your PayPal payment for Dea"
      />
      <div className="min-h-screen bg-[#FFFCF7] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          {status === 'processing' && (
            <>
              <Loader2 className="h-16 w-16 text-[#87A6A7] animate-spin mx-auto mb-6" />
              <h1 className="font-serif text-2xl text-[#726158] mb-2">Processing Payment</h1>
              <p className="text-[#AA9283]">Please wait while we verify your PayPal payment...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h1 className="font-serif text-2xl text-[#726158] mb-2">
                {isUpgrade ? 'Upgrade Complete!' : 'Payment Successful!'}
              </h1>
              <p className="text-[#AA9283] mb-4">
                {isUpgrade
                  ? "You're all set with full access. Picking up right where you left off."
                  : 'Your payment has been processed successfully.'}
              </p>
              {invitationCode && !isUpgrade && (
                <div className="bg-[#87A6A7]/10 rounded-lg p-4 mb-6">
                  <p className="text-[#AA9283] text-sm mb-1">Your Invitation Code:</p>
                  <p className="font-mono text-xl text-[#726158] font-bold">{invitationCode}</p>
                </div>
              )}
              <p className="text-[#AA9283] text-sm mb-4">
                {isUpgrade ? 'Redirecting you to Dea...' : 'Redirecting you to signup...'}
              </p>
              <Button
                onClick={() => navigate(isUpgrade ? '/chat' : `/signup?code=${invitationCode}`)}
                className="btn-gold"
                data-testid="paypal-success-continue-btn"
              >
                {isUpgrade ? 'Go to Dea' : 'Continue to Signup'}
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h1 className="font-serif text-2xl text-[#726158] mb-2">Payment Issue</h1>
              <p className="text-[#AA9283] mb-6">{error}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/checkout?plan=monthly')}
                  className="btn-gold w-full"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => window.location.href = 'mailto:support@beambitious.chat'}
                  variant="outline"
                  className="w-full"
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
}
